import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// material
import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  computeHierarchyMeta,
  findOptionByNameOrId,
  getChangedFields,
  parseValidationErrorMessage
} from '../../utils/Utilities';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { AddNewRegion } from '../../network/User';
import { SnackBarAlert } from '../../components/HelperComponents';
import { UpdateRegion } from '../network/Region';
import { APP_CONSTANTS } from '../../URLConstants';

// ----------------------------------------------------------------------

const style = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 800 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const RegionSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  organizationId: Yup.string().required('Required'),
  zoneOfficeId: Yup.string().required('Required'),
  headOfficeName: Yup.string().required('Required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
});

AddRegionModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  headOfficesList: PropTypes.object,
  zonesList: PropTypes.object,
  fetchZones: PropTypes.func,
  onUpdate: PropTypes.func
};

export default function AddRegionModal({
  opneAssignedModal,
  handleClose,
  headOfficesList,
  zonesList,
  fetchZones,
  selectedRegion,
  onUpdate
}) {
  const regionSelectedForUpdate = selectedRegion?.region
  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const { sessionUserData, accountDetails } = useAuth();
  const sessionUserPath = accountDetails?.path;
  const { isAdminUser, hierarchyInfo, isHeadOfficeLocked, isZoneOfficeLocked } = useMemo(
    () => computeHierarchyMeta(sessionUserPath, sessionUserData?.user?.role),
    [sessionUserPath, sessionUserData?.user?.role]
  );

  const addRegionFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: regionSelectedForUpdate?.name || '',
      organizationId: sessionUserData?.user?.organizationId,
      zoneOfficeId: regionSelectedForUpdate?.zoneOfficeId || '',
      zoneOfficeName: regionSelectedForUpdate?.zoneOfficeName || '',
      headOfficeName: regionSelectedForUpdate?.headOfficeName || '',
      address: regionSelectedForUpdate?.address || '',
      city: regionSelectedForUpdate?.city || '',
      state: regionSelectedForUpdate?.state || '',
      pincode: regionSelectedForUpdate?.pincode || '',
      contactPerson: regionSelectedForUpdate?.contactPerson || '',
      phone: regionSelectedForUpdate?.phone || '',
      email: regionSelectedForUpdate?.email || '',
      headOfficeId: headOfficesList?.[0]?.id || ''
    },
    validationSchema: RegionSchema,
    onSubmit: () => {
      const basePayload = {
        name: values?.name,
        organizationId: values?.organizationId,
        address: values?.address,
        city: values?.city,
        state: values?.state,
        pincode: values?.pincode,
        contactPerson: values?.contactPerson,
        phone: values?.phone,
        email: values?.email,
        zoneOfficeId: values?.zoneOfficeId,
      };

      let payload;

      if (regionSelectedForUpdate?.id) {
        const changedFields = getChangedFields(basePayload, regionSelectedForUpdate);

        if (Object.keys(changedFields).length === 0) {
          addRegionFormik.setSubmitting(false);
          onUpdate(APP_CONSTANTS?.NO_CHANGES, 'No changes detected');
          return;
        }

        payload = changedFields;
        updateRegion(regionSelectedForUpdate?.id, payload);
      } else {
        payload = Object.fromEntries(
          Object.entries(basePayload).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        createRegion(payload);
      }
    }
  });

  function createRegion(payload) {
    AddNewRegion(payload)
      .then((res) => {
        addRegionFormik.setSubmitting(false);
        handleClose();
        const message = `${addRegionFormik?.values?.name} successfully created !`;
        if (onUpdate) {
          onUpdate(APP_CONSTANTS?.REGION_UPDATE_SUCCESSFULL, message);
        } else {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: message,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);
        }

        addRegionFormik.resetForm();
      })
      .catch((error) => {
        addRegionFormik.setSubmitting(false);
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: parseValidationErrorMessage(error),
          severity: 'error'
        }));
        setIsSnackBarAlertOpen(true);
      });
  }

  function updateRegion(regionId, payload) {
    UpdateRegion(regionId, payload)
      .then((res) => {
        addRegionFormik.setSubmitting(false);
        handleClose();
        onUpdate(APP_CONSTANTS?.REGION_UPDATE_SUCCESSFULL, 'Region has been updated!');
        addRegionFormik.resetForm();
      })
      .catch((error) => {
        addRegionFormik.setSubmitting(false);
        onUpdate(APP_CONSTANTS?.REGION_UPDATE_UNSUCCESSFULL, parseValidationErrorMessage(error));
      });
  }

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
    addRegionFormik;

  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isAdminUser) return;

    if (hierarchyInfo?.headOffice && !values?.headOfficeName) {
      setFieldValue('headOfficeName', hierarchyInfo.headOffice);
    }

    if (hierarchyInfo?.zoneOffice && !values?.zoneOfficeName) {
      setFieldValue('zoneOfficeName', hierarchyInfo.zoneOffice);
    }
  }, [opneAssignedModal, isAdminUser, hierarchyInfo?.headOffice, hierarchyInfo?.zoneOffice]);

  // Load all zones when modal opens 
  useEffect(() => {
    if (!opneAssignedModal) return;
    fetchZones();
  }, [opneAssignedModal]);

  // Map Head Office name/id -> headOfficeId (required for HO/ZO/RO admins)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isHeadOfficeLocked) return;
    if (values?.headOfficeId) return;

    const desired = hierarchyInfo?.headOffice || values?.headOfficeName;
    const match = findOptionByNameOrId(headOfficesList, desired);
    if (!match) return;

    setFieldValue('headOfficeId', match.id);
    setFieldValue('headOfficeName', match.name || desired || '');
  }, [
    opneAssignedModal,
    isHeadOfficeLocked,
    hierarchyInfo?.headOffice,
    headOfficesList,
    values?.headOfficeId,
    values?.headOfficeName
  ]);

  // Map Zone Office name/id -> zoneOfficeId and load Regions (required for ZO admin, also safe for RO admin)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isZoneOfficeLocked) return;
    if (values?.zoneOfficeId) return;

    const desired = hierarchyInfo?.zoneOffice || values?.zoneOfficeName;
    const match = findOptionByNameOrId(zonesList, desired);
    if (!match) return;

    setFieldValue('zoneOfficeId', match.id);
    setFieldValue('zoneOfficeName', match.name || desired || '');
  }, [
    opneAssignedModal,
    isZoneOfficeLocked,
    hierarchyInfo?.zoneOffice,
    zonesList,
    values?.zoneOfficeId,
    values?.zoneOfficeName
  ]);

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider enableReinitialize="true" value={addRegionFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                  {regionSelectedForUpdate?.id ? 'Update Region' : 'Create New Region'}
                </Typography>
                <IconButton
                  onClick={() => {
                    handleClose();
                    addRegionFormik?.resetForm();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Grid container spacing={2} sx={{ my: 1 }}>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Region Name *"
                      id="zoneName"
                      name="zoneName"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('name')}
                      error={Boolean(touched?.name && errors?.name)}
                      helperText={touched?.name && errors?.name}
                      disabled={regionSelectedForUpdate?.id}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  {isHeadOfficeLocked ? (
                    <TextField
                      label="Head Office *"
                      value={values?.headOfficeName || ''}
                      disabled
                      error={Boolean(touched?.headOfficeName && errors?.headOfficeName)}
                      helperText={touched?.headOfficeName && errors?.headOfficeName}
                      inputProps={{ 'aria-label': 'Head office' }}
                      fullWidth
                    />
                  ) : (
                    <Autocomplete
                      options={headOfficesList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={headOfficesList.find((o) => o.id === values.headOfficeId) || null}
                      onChange={(e, newValue) => {
                        const selectedId = newValue ? newValue.id : '';
                        setFieldValue('headOfficeId', selectedId);
                        setFieldValue('zoneOfficeId', '');
                        setFieldValue('zoneOfficeName', '');
                        fetchZones(selectedId || undefined);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Head Office"
                          error={Boolean(touched?.headOfficeId && errors?.headOfficeId)}
                          helperText={touched?.headOfficeId && errors?.headOfficeId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Select Head office'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <Autocomplete
                    options={zonesList || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={zonesList.find((z) => z.id === values.zoneOfficeId) || null}
                    onChange={(e, newValue) => {
                      setFieldValue('zoneOfficeId', newValue ? newValue.id : '');
                      setFieldValue('zoneOfficeName', newValue ? newValue.name : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Zone Office"
                        error={Boolean(touched?.zoneOfficeId && errors?.zoneOfficeId)}
                        helperText={touched?.zoneOfficeId && errors?.zoneOfficeId}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Select Zone office'
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Pincode"
                      id="pincode"
                      name="pincode"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('pincode')}
                      error={Boolean(touched?.pincode && errors?.pincode)}
                      helperText={touched?.pincode && errors?.pincode}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="City"
                      id="city"
                      name="city"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('city')}
                      error={Boolean(touched?.city && errors?.city)}
                      helperText={touched?.city && errors?.city}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="State"
                      id="state"
                      name="state"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('state')}
                      error={Boolean(touched?.state && errors?.state)}
                      helperText={touched?.state && errors?.state}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('address')}
                      error={Boolean(touched?.address && errors?.address)}
                      helperText={touched?.address && errors?.address}
                      multiline
                      rows={2}
                      maxRows={4}
                      label="Address"
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Contact Person Name"
                      id="contactPerson"
                      name="contactPerson"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('contactPerson')}
                      error={Boolean(touched?.contactPerson && errors?.contactPerson)}
                      helperText={touched?.contactPerson && errors?.contactPerson}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      id="email"
                      name="email"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('email')}
                      error={Boolean(touched?.email && errors?.email)}
                      helperText={touched?.email && errors?.email}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Phone"
                      id="phone"
                      name="phone"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('phone')}
                      error={Boolean(touched?.phone && errors?.phone)}
                      helperText={touched?.phone && errors?.phone}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={addRegionFormik.isSubmitting}
                  sx={{ mt: 2, height: '40px', borderRadius: 2 }}
                  variant="contained"
                  type="submit"
                  size="large"
                >
                  {regionSelectedForUpdate?.id ? 'Update' : 'Create'}
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
